import { LoadingArea } from '@consigli/facade';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';

import { FeatureFlag, useFeatureFlag, useFeatureFlagsReady } from '@/feature-flags';
import { Route } from '@/routes';

import { ServiceListPage } from './service-list-page';

/**
 * Chooses between the current service list and the redesigned project overview.
 *
 * The path is untouched on purpose: `monopy`'s notification emails hardcode
 * `/projects/{id}/services/...`, so this URL keeps working and the index
 * forwards to the tabbed page rather than the route moving.
 *
 * Waits for `useFeatureFlagsReady` rather than reading the flag alone, for the
 * reason `project-list-route.tsx` gives: an unread flag resolves to `false`, so
 * a targeted user would see the old page paint and then be redirected away from
 * it. Without the wait this gate is worse than the project list's, since the
 * swap here is a navigation rather than a re-render.
 *
 * Flag-lifetime code. It goes when the old service list does.
 */
export const ServiceListRoute: FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const ready = useFeatureFlagsReady();
  const redesigned = useFeatureFlag(FeatureFlag.AppRedesign);

  if (!ready) {
    return (
      <div className="flex justify-center items-center">
        <LoadingArea title={t('service-list.loading-title')} className="mx-auto mt-[25vh]" />
      </div>
    );
  }

  // Absolute, and built from the raw param rather than `..`: these tab routes
  // have two-segment paths, so react-router's default `relative="route"` would
  // resolve `..` against the parent *route* — `projects` — and land on
  // /projects/agents. The route test pins the target for that reason.
  return redesigned ? (
    <Navigate replace to={`/${Route.PROJECTS}/${projectId}/${Route.AGENTS}`} />
  ) : (
    <ServiceListPage />
  );
};
